import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Icon from "@cloudscape-design/components/icon"
import Select, { type SelectProps } from "@cloudscape-design/components/select"
import Textarea from "@cloudscape-design/components/textarea"
import { useEffect, useMemo, useRef, useState } from "react"
import type { Comment } from "@/generated/sdk"
import { useComments, useCreateComment, useUpdateComment } from "@/hooks/queries/useCollaboration"
import { getAvatarColor, getInitials } from "@/utils/avatar"
import { GENERAL_ANCHOR, useDtaComments } from "./DtaCommentsContext"
import "./CommentsRail.css"

interface CommentsRailProps {
  dtaId: string
  /**
   * Company recorded on new comments: the DTA's vendor for an external caller, "DAE" for an
   * internal one.
   */
  vendorCompany: string
  /**
   * DTA version for scoping comments.
   */
  version: string
}

type AnchorKind = "general" | "section" | "field"

function anchorKind(sectionReference: string): AnchorKind {
  if (sectionReference === GENERAL_ANCHOR) return "general"
  return sectionReference.includes(" / ") ? "field" : "section"
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

/**
 * Side rail listing all DTA comment threads grouped by General / Section / Field. Field/section
 * markers open it focused on a specific anchor; the composer at the top posts to the focused anchor
 * (or General). Reuses the existing comment/reply/resolve mutations.
 */
export default function CommentsRail({ dtaId, vendorCompany, version }: CommentsRailProps) {
  const rail = useDtaComments()
  // Version-scoped read: the rail lists the version it posts to (see handlePost), so a comment
  // written here shows up in the list it just refreshed.
  const { data } = useComments(dtaId, version)
  const comments = useMemo(() => data?.comments ?? [], [data])
  const createComment = useCreateComment(dtaId)
  const updateComment = useUpdateComment(dtaId)

  const [filter, setFilter] = useState<SelectProps.Option>({ label: "All", value: "all" })
  const [draft, setDraft] = useState("")
  const [replyFor, setReplyFor] = useState<string | null>(null)
  const [replyText, setReplyText] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [topOffset, setTopOffset] = useState<number | null>(null)
  const focusedRef = useRef<HTMLDivElement>(null)

  const focusedAnchor = rail?.focusedAnchor ?? null
  const isolatedCommentId = rail?.isolatedCommentId ?? null
  const railOpen = rail?.railOpen ?? false

  // Pin the rail below the sticky top nav (measured), so its header/close button stay visible.
  useEffect(() => {
    if (!railOpen) return
    const nav = document.getElementById("top-nav")
    if (!nav) return
    const measure = () => setTopOffset(nav.getBoundingClientRect().height)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(nav)
    return () => ro.disconnect()
  }, [railOpen])

  // Scroll the focused thread into view when the rail opens focused on an anchor.
  useEffect(() => {
    if (rail?.railOpen && focusedAnchor) {
      requestAnimationFrame(() => {
        focusedRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
      })
    }
  }, [rail?.railOpen, focusedAnchor])

  const filtered = useMemo(() => {
    const kind = filter.value
    const sorted = [...comments].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )
    if (kind === "all") return sorted
    if (kind === "unresolved") return sorted.filter((c) => c.status === "PENDING")
    return sorted.filter((c) => anchorKind(c.section_reference) === kind)
  }, [comments, filter])

  // Group by anchor kind, preserving General -> Section -> Field ordering.
  const groups = useMemo(() => {
    const g: Record<AnchorKind, Comment[]> = { general: [], section: [], field: [] }
    for (const c of filtered) g[anchorKind(c.section_reference)].push(c)
    return g
  }, [filtered])

  if (!rail || !rail.railOpen) return null

  const composerAnchor = focusedAnchor ?? GENERAL_ANCHOR

  const handlePost = async () => {
    const text = draft.trim()
    if (!text) return
    setError(null)
    try {
      await createComment.mutateAsync({
        section_reference: composerAnchor,
        comment_text: text,
        vendor_company: vendorCompany,
        version,
      })
      setDraft("")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to post comment")
    }
  }

  const handleReply = async (commentId: string) => {
    const text = replyText.trim()
    if (!text) return
    setError(null)
    try {
      await updateComment.mutateAsync({ commentId, status: "PENDING", resolutionComment: text })
      setReplyFor(null)
      setReplyText("")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add reply")
    }
  }

  const handleResolve = async (commentId: string) => {
    setError(null)
    try {
      await updateComment.mutateAsync({ commentId, status: "RESOLVED" })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resolve comment")
    }
  }

  // A self-contained comment card: anchor pill + View link, identity, body, replies and actions all
  // inside one bordered card.
  const renderCard = (comment: Comment) => {
    const focused = comment.section_reference === focusedAnchor && !isolatedCommentId
    const kind = anchorKind(comment.section_reference)
    return (
      <div
        key={comment.comment_id}
        ref={focused ? focusedRef : undefined}
        className={`rail-card${focused ? " rail-card-focused" : ""}`}
      >
        {/* Anchor row: which section/field this comment is on, plus a link to view it in the editor. */}
        {kind !== "general" ? (
          <div className="rail-card-anchor">
            <span className="rail-anchor-badge" title={comment.section_reference}>
              {comment.section_reference}
            </span>
            <button
              type="button"
              className="rail-anchor-go"
              onClick={() => {
                rail?.navigateToAnchor(comment.section_reference)
                rail?.openRailForComment(comment.comment_id)
              }}
              title={`View ${comment.section_reference}`}
            >
              <Icon name="external" size="small" /> View
            </button>
          </div>
        ) : (
          <div className="rail-card-anchor">
            <span className="rail-anchor-badge rail-anchor-badge-general">General</span>
          </div>
        )}

        <div className="rail-card-head">
          <span
            className="rail-avatar"
            style={{ background: getAvatarColor(comment.user_id) }}
            aria-hidden="true"
          >
            {getInitials(comment.user_id)}
          </span>
          <div className="rail-thread-meta">
            <span className="rail-author">{comment.user_id}</span>
            <span className="rail-time">{formatDateTime(comment.created_at)}</span>
          </div>
          <Badge color={comment.status === "PENDING" ? "blue" : "green"}>
            {comment.status === "PENDING" ? "Unresolved" : "Resolved"}
          </Badge>
        </div>

        <p className="rail-comment-text">{comment.comment_text}</p>

        {comment.replies && comment.replies.length > 0 && (
          <div className="rail-replies">
            {comment.replies.map((reply, idx) => (
              <div key={idx} className="rail-reply">
                <span className="rail-reply-meta">
                  {reply.author} · {formatDateTime(reply.timestamp)}
                </span>
                <span className="rail-reply-text">{reply.text}</span>
              </div>
            ))}
          </div>
        )}

        <div className="rail-card-actions">
          {comment.status === "PENDING" &&
            (replyFor === comment.comment_id ? (
              <div className="rail-reply-form">
                <Textarea
                  value={replyText}
                  onChange={({ detail }) => setReplyText(detail.value)}
                  placeholder="Write a reply…"
                  rows={2}
                />
                <div className="rail-reply-form-actions">
                  <Button
                    variant="link"
                    onClick={() => {
                      setReplyFor(null)
                      setReplyText("")
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    loading={updateComment.isPending}
                    disabled={!replyText.trim()}
                    onClick={() => void handleReply(comment.comment_id)}
                  >
                    Comment
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Button
                  variant="link"
                  onClick={() => {
                    setReplyFor(comment.comment_id)
                    setReplyText("")
                  }}
                >
                  Reply
                </Button>
                <Button
                  variant="normal"
                  loading={updateComment.isPending}
                  onClick={() => void handleResolve(comment.comment_id)}
                >
                  Resolve
                </Button>
              </>
            ))}
        </div>
      </div>
    )
  }

  const GROUP_LABEL: Record<AnchorKind, string> = {
    general: "General",
    section: "Section",
    field: "Field",
  }

  const isolatedComment = isolatedCommentId
    ? comments.find((c) => c.comment_id === isolatedCommentId)
    : null

  return (
    <aside
      className="comments-rail"
      aria-label="Comments"
      style={topOffset != null ? { top: topOffset } : undefined}
    >
      <div className="rail-header">
        <h3 className="rail-title">Comments</h3>
        <Button
          variant="icon"
          iconName="close"
          ariaLabel="Close comments"
          onClick={rail.closeRail}
        />
      </div>

      {isolatedCommentId ? (
        /* Isolation mode: a single comment thread with a back link to the full list. */
        <div className="rail-isolated">
          <button type="button" className="rail-back" onClick={rail.clearIsolation}>
            <Icon name="angle-left" size="small" /> All comments
          </button>
          {error && <Box color="text-status-error">{error}</Box>}
          {isolatedComment ? (
            renderCard(isolatedComment)
          ) : (
            <div className="rail-empty">This comment is no longer available.</div>
          )}
        </div>
      ) : (
        <>
          <div className="rail-controls">
            <Select
              selectedOption={filter}
              onChange={({ detail }) => setFilter(detail.selectedOption)}
              options={[
                { label: "All", value: "all" },
                { label: "Unresolved", value: "unresolved" },
                { label: "General", value: "general" },
                { label: "Section", value: "section" },
                { label: "Field", value: "field" },
              ]}
            />
          </div>

          {/* Composer scoped to the focused anchor (or General). */}
          <div className="rail-composer">
            <div className="rail-composer-anchor">
              Commenting on: <strong>{composerAnchor}</strong>
            </div>
            <Textarea
              value={draft}
              onChange={({ detail }) => setDraft(detail.value)}
              placeholder={`Add a comment on ${composerAnchor}…`}
              rows={2}
            />
            <div className="rail-composer-actions">
              <Button
                variant="primary"
                loading={createComment.isPending}
                disabled={!draft.trim()}
                onClick={() => void handlePost()}
              >
                Comment
              </Button>
            </div>
          </div>

          {error && <Box color="text-status-error">{error}</Box>}

          <div className="rail-groups">
            {(["general", "section", "field"] as AnchorKind[]).map((kind) =>
              groups[kind].length > 0 ? (
                <section key={kind} className="rail-group">
                  <h4 className="rail-group-title">{GROUP_LABEL[kind]}</h4>
                  {groups[kind].map((c) => renderCard(c))}
                </section>
              ) : null,
            )}
            {filtered.length === 0 && <div className="rail-empty">No comments yet.</div>}
          </div>
        </>
      )}
    </aside>
  )
}
